import { DefiBalancesQuery } from './accounts';
import { getRequest, patchRequest, postRequest } from './request';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AssetRollForwardRealizedGainLossReportDto } from '@/pages/reports/fasb/fasb.types';

export enum SafeHavenStatus {
  NOT_STARTED = 'NOT_STARTED',
  OPTED_OUT = 'OPTED_OUT',
  STARTED = 'STARTED',
  ASSIGNING = 'ASSIGNING',
  APPROVED = 'APPROVED',
  COMPLETED = 'COMPLETED',
}

export interface GainLossReportAssetDto {
  from?: string;
  to?: string;
  tokenIds?: string[];
}
export interface GainLossReportAccountDto {
  from?: string;
  to?: string;
  tokenIds?: string[];
  accounts?: string[];
  sections?: string[];
}

export interface GainLossReportExportDto {
  from?: string;
  to?: string;
  tokenIds?: string[];
  accounts?: string[];
  sections?: string[];
}

export interface ExportGainLossto {
  from?: string;
  to?: string;
  tokenIds?: string[] | number[];
  accounts?: string[];
  sections?: string[];
  nftCollectionIds?: string[];
}
export interface CostBasisto {
  from?: string;
  to?: string;
  tokenIds?: string[];
  remainingValue?: string;
}
export interface TaxLotSafeHavenConfigDto {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  startDate?: string;
  endDate?: string;
  status: SafeHavenStatus;
  downloadUrl?: string;
}

export interface CreateTaxLotSafeHavenConfigDto
  extends Pick<TaxLotSafeHavenConfigDto, 'startDate' | 'endDate'> {
  status?: SafeHavenStatus;
}

export interface UpdateTaxLotSafeHavenConfigDto
  extends Pick<TaxLotSafeHavenConfigDto, 'startDate' | 'endDate'> {
  status?: SafeHavenStatus;
}

// Tokens tab
type ExportClosingPositionsTokensReportParams = {
  endpoint: 'asset';
  dto: {
    date?: string;
    tokens?: string;
  };
};

// NFT Collections tab
type ExportClosingPositionsNftCollectionsReportParams = {
  endpoint: 'nft';
  dto: {
    date?: string;
    collections?: string[];
  };
};

// Wallets tab
type ExportClosingPositionsWalletsReportParams = {
  endpoint: 'account';
  dto: {
    date?: string;
    accounts?: string[];
    sections?: string[];
    collections?: string[];
    tokens?: string;
  };
};

// DeFi tab
type ExportClosingPositionsDefiReportParams = {
  endpoint: 'defi';
  dto: DefiBalancesQuery;
};

export type ExportClosingPositionsReportParams =
  | ExportClosingPositionsTokensReportParams
  | ExportClosingPositionsNftCollectionsReportParams
  | ExportClosingPositionsWalletsReportParams
  | ExportClosingPositionsDefiReportParams;

type WalletTokenBalancesReportDto = {
  snapshotGroupId: string;
  from?: string; // 2025-08-06: currently does nothing
  to?: string; // 2025-08-06: currently does nothing
};

export const reportsQueryKeys = {
  all: ['reports'] as const,
  lists: () => [...reportsQueryKeys.all, 'lists'] as const,

  fasbLists: () => [...reportsQueryKeys.all, 'fasbLists'] as const,
  fasbRollForward: (dto: GainLossReportAccountDto) =>
    [...reportsQueryKeys.fasbLists(), dto] as const,
  taxLotSafeHaven: (orgId: string) => ['taxLotSafeHaven', orgId] as const,
};

export const exportClosingPostionsReport = ({
  endpoint,
  dto,
}: ExportClosingPositionsReportParams) =>
  getRequest<string>(`/reports/closing-positions/${endpoint}`, dto, {
    Accept: 'application/json,text/csv',
  });

export const exportCostBasis = (dto: CostBasisto) =>
  postRequest<string>(`/reports/tax-lot`, dto, { Accept: 'application/json,text/csv' });

export const exportGainLoss = (dto: ExportGainLossto) =>
  postRequest<string>(`/reports/gain-loss`, dto);

export const exportRealizedGainLoss = (endpoint: string, dto: GainLossReportExportDto) =>
  getRequest<string>(`/reports/realized-gain-loss/${endpoint}`, dto, {
    Accept: 'text/csv',
  });

export const getGainLossAsset = (dto: GainLossReportAssetDto) =>
  getRequest<string>(`/reports/realized-gain-loss/asset`, dto);

export const getGainLossAccount = (dto: GainLossReportAccountDto) =>
  getRequest<string>(`/reports/realized-gain-loss/account`, dto);

export const downloadReport = (id: string) =>
  getRequest<string>(`/reports/${id}`, {}, { Accept: 'text/csv' });

export const getReportHistory = () => getRequest<string>(`/reports/history`, {});

export const exportAccounts = () =>
  postRequest<string>('/reports/accounts', {}, { Accept: 'text/csv' });

export const exportContacts = () =>
  postRequest<string>('/reports/contacts', {}, { Accept: 'text/csv' });

export const exportWalletTokenBalances = (dto: WalletTokenBalancesReportDto) =>
  getRequest<string>('/reports/wallet-token-balances', dto, { Accept: 'text/csv' });

export const exportRoyaltyInvoiceTransactions = (dto: { invoiceId: string }) =>
  postRequest<string>('/reports/royalty-invoice-transactions', dto, { Accept: 'text/csv' });

export const _getFasbRollForward = (dto: GainLossReportAccountDto) =>
  getRequest<string>(`/reports/fasb/roll-forward`, dto);
export const useGetFasbRollForward = (dto: GainLossReportAccountDto) => {
  return useQuery<AssetRollForwardRealizedGainLossReportDto[], Error>({
    enabled: !!dto.from && !!dto.to,
    queryKey: reportsQueryKeys.fasbRollForward(dto),
    queryFn: async () => {
      const { body } = await _getFasbRollForward(dto);
      return body.data;
    },
  });
};

export const exportFasbRollForward = (dto: GainLossReportAccountDto) =>
  getRequest<string>('/reports/fasb/roll-forward', dto, { Accept: 'text/csv' });

export const _getTaxLotSafeHaven = () =>
  getRequest<TaxLotSafeHavenConfigDto>('/tax/safe-haven', null);
export const useGetTaxLotSafeHaven = (orgId: string) => {
  return useQuery<TaxLotSafeHavenConfigDto, Error>({
    queryKey: reportsQueryKeys.taxLotSafeHaven(orgId),
    queryFn: async () => {
      const { body } = await _getTaxLotSafeHaven();
      return body.data;
    },
  });
};

export const _createTaxLotSafeHaven = (dto: CreateTaxLotSafeHavenConfigDto) =>
  postRequest<TaxLotSafeHavenConfigDto>('/tax/safe-haven', dto);
export const useCreateTaxLotSafeHaven = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dto }: { orgId: string; dto: CreateTaxLotSafeHavenConfigDto }) => {
      const { body } = await _createTaxLotSafeHaven(dto);
      return body.data;
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries(reportsQueryKeys.taxLotSafeHaven(orgId));
    },
  });
};

export const _updateTaxLotSafeHaven = (dto: UpdateTaxLotSafeHavenConfigDto) =>
  patchRequest<TaxLotSafeHavenConfigDto>('/tax/safe-haven', dto);
export const useUpdateTaxLotSafeHaven = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dto }: { orgId: string; dto: UpdateTaxLotSafeHavenConfigDto }) => {
      const { body } = await _updateTaxLotSafeHaven(dto);
      return body.data;
    },
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries(reportsQueryKeys.taxLotSafeHaven(orgId));
    },
  });
};
